// app/(app)/invoices/bulk-pdf.tsx
import { Button } from "@/components/default/forms";
import DatePicker from "@/components/default/forms/DatePicker";
import SearchableSelect from "@/components/default/forms/SearchableSelect";
import { useAppTheme } from "@/hooks/useAppTheme";
import { invoiceApi } from "@/services/invoiceApi";
import { locationApi } from "@/services/locationApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchCustomers } from "@/store/slices/customerSlice";
import { fetchEmployees } from "@/store/slices/employeeSlice";
import { showAlert } from "@/utils/alert";
import * as FileSystem from "expo-file-system/legacy";
import { useFocusEffect } from "expo-router";
import * as Sharing from "expo-sharing";
import { useCallback, useEffect, useState } from "react";
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

export default function BulkPdfScreen() {
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { employees } = useAppSelector((state) => state.employees);
  const { customers } = useAppSelector((state) => state.customers);
  const isOwner = user?.userType === "Owner";
  const isEmployee = user?.userType === "Employee";

  const [useToday, setUseToday] = useState(false);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const [selectedAreaId, setSelectedAreaId] = useState<string>("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");

  const [loading, setLoading] = useState(false);

  // Areas
  const [areas, setAreas] = useState<{ id: number; name: string }[]>([]);
  const [loadingAreas, setLoadingAreas] = useState(false);

  // Reset all form fields on focus
  useFocusEffect(
    useCallback(() => {
      setUseToday(false);
      setStartDate("");
      setEndDate("");
      setSelectedAreaId("");
      setSelectedEmployeeId("");
      setSelectedCustomerId("");
      loadAreas();
      dispatch(fetchEmployees({ page: 1, limit: 100, search: "" }));
      dispatch(fetchCustomers({ page: 1, limit: 100, search: "" }));
    }, []),
  );

  // Load areas, employees (only for Owner), and customers
  // useCallback(() => {
  //   loadAreas();
  //   // if (isOwner || isEmployee) {
  //   dispatch(fetchEmployees({ page: 1, limit: 100, search: "" }));
  //   // }
  //   dispatch(fetchCustomers({ page: 1, limit: 100, search: "" }));
  // }, []);

  const loadAreas = async () => {
    setLoadingAreas(true);
    try {
      const fetchedAreas = await locationApi.getAreas();
      setAreas(fetchedAreas);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingAreas(false);
    }
  };

  // Fetch customers when area changes (for customer dropdown)
  useEffect(() => {
    if (selectedAreaId) {
      dispatch(
        fetchCustomers({
          page: 1,
          limit: 100,
          search: "",
          areaId: parseInt(selectedAreaId),
        }),
      );
      setSelectedCustomerId("");
    }
  }, [selectedAreaId]);

  const buildParams = () => {
    const params: any = {};
    if (useToday) {
      params.today = true;
    } else {
      if (!startDate || !endDate) return null;
      params.startDate = startDate;
      params.endDate = endDate;
    }
    if (selectedAreaId) {
      params.areaId = parseInt(selectedAreaId);
    }
    if (selectedEmployeeId) {
      params.createdBy = parseInt(selectedEmployeeId);
    }
    if (!isOwner && employees.length <= 0 && user?.id) {
      // Employees see only their own invoices
      params.createdBy = user.id;
    }
    if (selectedCustomerId) {
      params.customerId = parseInt(selectedCustomerId);
    }
    return params;
  };

  const handleDownload = async () => {
    const params = buildParams();
    if (!params) {
      showAlert("Error", "Please select both start and end dates");
      return;
    }
    setLoading(true);
    try {
      const blob = await invoiceApi.bulkPdf(params);
      if (Platform.OS === "web") {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } else {
        const reader = new FileReader();
        reader.onload = async () => {
          const base64 = (reader.result as string).split(",")[1];
          const fileUri =
            FileSystem.cacheDirectory + `invoices_bulk_${Date.now()}.pdf`;
          await FileSystem.writeAsStringAsync(fileUri, base64, {
            encoding: FileSystem.EncodingType.Base64,
          });
          if (await Sharing.isAvailableAsync()) {
            await Sharing.shareAsync(fileUri);
          } else {
            showAlert("Error", "Sharing not available");
          }
        };
        reader.readAsDataURL(blob);
      }
    } catch (error: any) {
      showAlert("Error", error.message || "Failed to generate PDF");
    } finally {
      setLoading(false);
    }
  };

  const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    keyboardAvoid: { flex: 1 },
    scrollContent: { padding: spacing.lg, paddingBottom: spacing.xl },
    title: {
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.md,
    },
    subtitle: {
      color: colors.textLight,
      marginBottom: spacing.md,
    },
    switchRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: spacing.md,
    },
    switchLabel: { fontSize: 16, color: colors.text },
    filterRow: {
      flexDirection: "row",
      gap: spacing.sm,
      marginBottom: spacing.md,
    },
    filterButton: {
      flex: 1,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: spacing.sm,
      alignItems: "center",
    },
    filterText: { fontSize: 14, color: colors.text },
    inputContainer: { marginBottom: spacing.md },
    buttonRow: { marginTop: spacing.lg },
  });

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.keyboardAvoid}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <Text style={styles.title}>Bulk Invoices PDF</Text>
          <Text style={styles.subtitle}>
            Generate a single PDF containing one invoice per page for the
            selected filters.
          </Text>

          <View style={styles.switchRow}>
            <Text style={styles.switchLabel}>Today's invoices</Text>
            <Switch
              value={useToday}
              onValueChange={(val) => {
                setUseToday(val);
                if (val) {
                  setStartDate("");
                  setEndDate("");
                }
              }}
              trackColor={{ false: colors.border, true: colors.primary }}
              thumbColor={colors.background}
            />
          </View>

          {!useToday && (
            <View style={styles.filterRow}>
              <DatePicker
                value={startDate}
                onChange={setStartDate}
                placeholder="Start Date"
                containerStyle={{ flex: 1, marginBottom: 0 }}
              />
              <DatePicker
                value={endDate}
                onChange={setEndDate}
                placeholder="End Date"
                containerStyle={{ flex: 1, marginBottom: 0 }}
              />
              {(startDate || endDate) && (
                <TouchableOpacity
                  style={[styles.filterButton, { borderColor: colors.danger }]}
                  onPress={() => {
                    setStartDate("");
                    setEndDate("");
                  }}
                >
                  <Text style={{ color: colors.danger }}>Clear</Text>
                </TouchableOpacity>
              )}
            </View>
          )}

          {/* Area Filter */}
          <SearchableSelect
            label="Area / Route"
            placeholder="All areas"
            options={areas.map((a) => ({
              label: a.name,
              value: a.id.toString(),
            }))}
            value={selectedAreaId}
            onValueChange={setSelectedAreaId}
            loading={loadingAreas}
            containerStyle={styles.inputContainer}
            clearable
          />

          {/* Employee Filter – only for Owner */}
          {/* {isOwner && ( */}
          <SearchableSelect
            label="Created By (Employee)"
            placeholder="Select employee (optional)"
            options={employees.map((emp) => ({
              label: `${emp.ownerName} (${emp.employeeCode})`,
              value: emp.id.toString(),
            }))}
            value={selectedEmployeeId}
            onValueChange={setSelectedEmployeeId}
            containerStyle={styles.inputContainer}
          />
          {/* )} */}

          {/* Customer Filter – filtered by area if area selected */}
          <SearchableSelect
            label="Customer (Optional)"
            placeholder="Select customer"
            options={customers.map((c) => ({
              label: `${c.fullName} (${c.phone})`,
              value: c.id.toString(),
            }))}
            value={selectedCustomerId}
            onValueChange={setSelectedCustomerId}
            containerStyle={styles.inputContainer}
            disabled={!selectedAreaId}
            clearable
          />

          <View style={styles.buttonRow}>
            <Button
              title="Download Bulk PDF"
              onPress={handleDownload}
              loading={loading}
              variant="primary"
            />
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}
